import Loader from "atoms/loaders/loader";
import PublicationTypes from "constants/publication-types";
import ArticleRecord from "models/view-models/article-record";
import CurrentPublicationEntityRecord from "models/view-models/atoms/current-publication-entity-record";
import UnorderedList from "molecules/lists/unordered-list";
import HiddenContentAlertBanner from "organisms/alerts/hidden-content-alert-banner";
import CaapsItemTitleAreaWithinPanel from "organisms/publication-content/notebook-reference-panel/caaps-title-area-within-panel";
import PublicationContentAreaWithinPanel from "organisms/publication-content/notebook-reference-panel/publication-content-area-within-panel";
import useVirtualTableOfContents from "pages/notebooks/notebook-reference-panel/bookview/use-virtual-table-of-contents";
import React, { useEffect, useMemo, useState } from "react";
import CurrentPublicationEntityAtom from "utilities/atoms/current-publication-entity-atom";
import { CollectionUtils } from "utilities/collection-utils";
import useCurrentPublication from "utilities/contexts/use-current-publication";
import PublicationComponentType from "utilities/enumerations/publication-component-type";
import useUpdateAtomEffect from "utilities/hooks/atoms/use-update-atom-value-effect";
import useArticles from "utilities/hooks/domain/publications/chapters/articles/use-articles";
import useSections from "utilities/hooks/domain/publications/chapters/sections/use-sections";
import useChapter from "utilities/hooks/domain/publications/chapters/use-chapter";
import useDebounce from "utilities/hooks/use-debounce";
import { useErrorBoundary } from "utilities/hooks/use-error-boundary";
import useErrors from "utilities/hooks/use-errors";
import useLoading from "utilities/hooks/use-loading";
import { useLocalization } from "utilities/hooks/use-localization";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface ChapterContentWithinPanelProps {}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASS_NAME = "c-publication-page-layout";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ChapterContentWithinPanel: React.FunctionComponent<
    ChapterContentWithinPanelProps
> = (props: ChapterContentWithinPanelProps) => {
    const { publication } = useCurrentPublication();
    const { currentTocItem } = useVirtualTableOfContents();
    const isVirtual = !!currentTocItem;
    const chapterId = currentTocItem?.parentId;
    const nfpaLabel = currentTocItem?.nfpaLabel;
    const { t } = useLocalization();
    const [shouldLoadSections, setShouldLoadSections] =
        useState<boolean>(false);
    const loaderText = t("loadingItem", {
        item: t("chapter"),
    });
    // -----------------------------------------------------------------------------------------
    // #region Service Hooks
    // -----------------------------------------------------------------------------------------
    const {
        resultObject: chapter,
        loading: chapterLoading,
        loaded: chapterLoaded,
        errors: chapterErrors,
    } = useChapter({
        chapterId: chapterId,
        nfpaLabel: nfpaLabel ?? "",
        publicationId: publication?.id,
    });

    const {
        resultObject: articles,
        loading: articlesLoading,
        loaded: articlesLoaded,
        errors: articleErrors,
    } = useArticles(publication?.id, chapter.id);

    const {
        resultObject: sections,
        loading: sectionsLoading,
        errors: sectionErrors,
    } = useSections(
        shouldLoadSections || !!currentTocItem ? publication?.id : undefined,
        shouldLoadSections || !!currentTocItem ? chapter.id : undefined
    );
    // #endregion Service Hooks

    // -----------------------------------------------------------------------------------------
    // #region Side Effects
    // -----------------------------------------------------------------------------------------

    const loading = useLoading(
        articlesLoading,
        chapterLoading,
        sectionsLoading
    );
    const loadingDebounced = useDebounce(loading, 50);

    useEffect(() => {
        if (!articlesLoaded) {
            return;
        }

        // If articles have been loaded but the array is still empty, it might mean this Chapter only
        // contains sections. Load them now.
        if (CollectionUtils.isEmpty(articles)) {
            setShouldLoadSections(true);
        }
    }, [articles, articlesLoaded]);

    const errors = useErrors(chapterErrors, sectionErrors, articleErrors);
    useErrorBoundary(errors);

    useUpdateAtomEffect({
        atom: CurrentPublicationEntityAtom,
        enabled: chapter != null && chapterLoaded,
        value: new CurrentPublicationEntityRecord({
            entityId: chapter.id,
            entityTitle: chapter.getDisplayTitle(),
            selected: true,
            type: "Chapter",
        }),
    });

    // #endregion Side Effects

    // -----------------------------------------------------------------------------------------
    // #region Render
    // -----------------------------------------------------------------------------------------
    const titleArea = useMemo(() => {
        const determineArticle = () => {
            if (
                currentTocItem?.publicationType ===
                PublicationComponentType.Section
            ) {
                const section = sections.find(
                    (section) => section.id === currentTocItem?.id
                );
                return articles.find(
                    (article) => article.id === section?.articleId
                );
            }
            return articles.find(
                (article) => article.id === currentTocItem?.id
            );
        };
        if (loading) return <></>;
        if (publication.type === PublicationTypes.NEC) {
            return (
                <CaapsItemTitleAreaWithinPanel
                    titleClassName={""}
                    record={determineArticle() ?? new ArticleRecord()}
                    publication={publication}
                    externalId={determineArticle()?.externalId ?? ""}
                    publicationComponentType={PublicationComponentType.Article}
                />
            );
        }
        if (publication.type === PublicationTypes.NFC) {
            return (
                <CaapsItemTitleAreaWithinPanel
                    titleClassName={""}
                    record={chapter}
                    publication={publication}
                    externalId={chapter.externalId}
                    publicationComponentType={PublicationComponentType.Chapter}
                />
            );
        }
    }, [
        articles,
        chapter,
        currentTocItem?.id,
        currentTocItem?.publicationType,
        loading,
        publication,
        sections,
    ]);

    if (publication == null) {
        // Failed to load publication from context
        return null;
    }

    const displaySections = () => {
        if (publication.type === PublicationTypes.NFC || isVirtual) {
            return sections.map((s) => s.with({ publication }));
        }
        return [];
    };

    return (
        <React.Fragment>
            {
                // if
                loadingDebounced && <Loader accessibleText={loaderText} />
            }
            {
                // if
                CollectionUtils.hasValues(errors) && (
                    <UnorderedList
                        listItems={errors}
                        cssClassName={`${BASE_CLASS_NAME}__errors`}
                    />
                )
            }
            {
                // if
                !loadingDebounced && (
                    <>
                        {!isVirtual && (
                            <HiddenContentAlertBanner
                                id={chapter.id}
                                type={PublicationComponentType.Chapter}
                            />
                        )}
                        <PublicationContentAreaWithinPanel
                            titleArea={titleArea}
                            sections={displaySections()}
                            externalId={chapter.externalId}
                        />
                    </>
                )
            }
        </React.Fragment>
    );

    // #endregion Render
};
// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default ChapterContentWithinPanel;

// #endregion Exports
